import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../ui/Icon'
import { Button } from '../../../../buttons/Button'
import { SummarySlotRow } from '../summaryComponents/SummarySlotRow'
import { SummaryPricingDetails } from '../summaryComponents/SummaryPricingDetails'
import ManagementPaymentMethodTabs from '../summaryComponents/ManagementPaymentMethodTabs'

import { Money, SummaryLine } from '../../../../../types/booking/booking'
import { PaymentMethod } from '../../../../../types/payment/payment'
import { formatMoney } from '../../../../../utils/booking/booking'

interface MobileSummaryPanelProps {
  lines: SummaryLine[]
  total?: Money
  pointsAmount?: number
  onClear: () => void
  onConfirm: () => void
  showPrices: boolean
  showPrepaid: boolean
  showConfirm: boolean
  totalSubtitle?: string
  confirmLabel: string
  confirmDisabled: boolean
  loading: boolean
  paymentMethod?: PaymentMethod
  onPaymentMethodChange?: (paymentMethod: PaymentMethod) => void
  prepaidCount: number
  paidCount: number
  options?: ReactNode
}

const MobileSummaryPanel = ({
  lines,
  total,
  pointsAmount,
  onClear,
  onConfirm,
  showPrices,
  showPrepaid,
  showConfirm,
  totalSubtitle,
  confirmLabel,
  confirmDisabled,
  loading,
  paymentMethod,
  onPaymentMethodChange,
  prepaidCount,
  paidCount,
  options,
}: MobileSummaryPanelProps) => {
  const { t } = useTranslation('bookings')
  const [expanded, setExpanded] = useState(false)

  const mobileToggle = (
    <Button
      label={t('booking.booking-courts.summary-panel.title')}
      icon={<Icon name={expanded ? 'chev' : 'chevUp'} size={20} />}
      iconOnly
      variant="ghost"
      className="summary-mobile-toggle rounded-circle"
      aria-expanded={expanded}
      onClick={() => setExpanded(value => !value)}
    />
  )

  const mobileInfo = (
    <div className="summary-mobile-info">
      <span className="summary-mobile-count">
        {t('booking.booking-courts.summary-panel.selected-slots', { itemsLength: lines.length })}
      </span>
      {showPrices && <strong>{total ? formatMoney(total) : '—'}</strong>}
    </div>
  )

  const confirmButton = showConfirm ? (
    <Button
      label={confirmLabel}
      icon={<Icon name="arrowR" size={16} />}
      iconPosition="right"
      size="md"
      loading={loading}
      disabled={confirmDisabled || loading}
      onClick={onConfirm}
    />
  ) : null

  return (
    <>
      <div
        className={`${paymentMethod ? 'with-payment-method' : ''}`}
        aria-hidden="true"
      />

      <div className="summary-mobile-wrapper">
        {expanded && (
          <div className="summary-mobile-expanded">
            <div className="ph-row ph-between mb-8">
              <div className="eyebrow">{t('booking.booking-courts.summary-panel.title')}</div>
              <Button
                label={t('booking.booking-courts.summary-panel.clear-btn')}
                variant="ghost"
                size="sm"
                disabled={loading}
                onClick={onClear}
              />
            </div>

            {lines.map(line => (
              <SummarySlotRow key={line.key} line={line} showPrices={showPrices} />
            ))}

            {showPrices && (
              <SummaryPricingDetails
                prepaidCount={prepaidCount}
                paidCount={paidCount}
                total={total}
                pointsAmount={pointsAmount}
                showPrepaid={showPrepaid}
                totalSubtitle={totalSubtitle}
              />
            )}

            {options}
          </div>
        )}

        <div className={`summary-mobile-bar ${paymentMethod ? 'with-payment-method' : ''}`}>
          {showConfirm && paymentMethod && onPaymentMethodChange ? (
            <>
              <ManagementPaymentMethodTabs
                value={paymentMethod}
                disabled={loading}
                onChange={onPaymentMethodChange}
              />
              <div className="summary-mobile-confirm-row">
                {mobileToggle}
                {mobileInfo}
                {confirmButton}
              </div>
            </>
          ) : (
            <>
              {mobileToggle}
              {mobileInfo}
              {confirmButton}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default MobileSummaryPanel
